import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, MapPin, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BUSINESS_INFO } from '@/lib/constants';
import { cn } from '@/lib/utils';

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navLinks = [
        { href: '/', label: 'Ana Sayfa' },
        { href: '/hizmetler', label: 'Hizmetler' },
        { href: '/iletisim', label: 'İletişim' },
    ];

    return (
        <>
            {/* Top bar - Contact info */}
            <div className="hidden md:block bg-primary text-primary-foreground">
                <div className="container-custom py-2">
                    <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-6">
                            <span className="flex items-center gap-2">
                                <MapPin className="h-4 w-4" />
                                {BUSINESS_INFO.addressShort}
                            </span>
                            <span className="flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                {BUSINESS_INFO.workingHours.weekdays}
                            </span>
                        </div>
                        <a 
                            href={`tel:${BUSINESS_INFO.phone}`}
                            className="flex items-center gap-2 font-medium hover:opacity-90 transition-opacity"
                        >
                            <Phone className="h-4 w-4" />
                            {BUSINESS_INFO.phoneDisplay}
                        </a>
                    </div>
                </div>
            </div>

            {/* Main header */}
            <header 
                className={cn(
                    "sticky top-0 z-50 w-full transition-all duration-300",
                    isScrolled 
                        ? "bg-background/95 backdrop-blur-md shadow-md" 
                        : "bg-background"
                )}
            >
                <div className="container-custom">
                    <div className="flex h-16 md:h-20 items-center justify-between">
                        {/* Logo */}
                        <Link to="/" className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary text-primary-foreground font-bold text-lg md:text-xl font-heading">
                                AT
                            </div>
                            <div className="flex flex-col">
                                <span className="font-heading font-bold text-lg md:text-xl">
                                    <span className="text-[hsl(45_90%_45%)]">Altın</span>{' '}
                                    <span className="text-foreground">Teknik</span>
                                </span>
                                <span className="text-xs text-muted-foreground hidden sm:block">
                                    {BUSINESS_INFO.tagline}
                                </span>
                            </div>
                        </Link>

                        {/* Desktop Navigation */}
                        <nav className="hidden md:flex items-center gap-8">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.href}
                                    to={link.href}
                                    className={cn(
                                        "text-sm font-medium transition-colors hover:text-primary",
                                        location.pathname === link.href
                                            ? "text-primary"
                                            : "text-muted-foreground"
                                    )}
                                >
                                    {link.label}
                                </Link>
                            ))}
                        </nav>

                        {/* Desktop CTA Buttons */}
                        <div className="hidden md:flex items-center gap-3">
                            <Button
                                variant="call"
                                size="default"
                                asChild
                            >
                                <a href={`tel:${BUSINESS_INFO.phone}`}>
                                    <Phone className="h-4 w-4" />
                                    Hemen Ara
                                </a>
                            </Button>
                            <Button
                                variant="whatsapp"
                                size="default"
                                asChild
                            >
                                <a 
                                    href={`https://wa.me/${BUSINESS_INFO.whatsapp}?text=Merhaba, klima hizmeti hakkında bilgi almak istiyorum.`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                    </svg>
                                    WhatsApp
                                </a>
                            </Button>
                        </div>

                        {/* Mobile Menu Button */}
                        <button
                            className="md:hidden p-2 rounded-lg hover:bg-muted transition-colors"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            aria-label="Menüyü aç/kapat"
                        >
                            {isMenuOpen ? (
                                <X className="h-6 w-6 text-foreground" />
                            ) : (
                                <Menu className="h-6 w-6 text-foreground" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {isMenuOpen && (
                    <div className="md:hidden border-t border-border bg-background">
                        <div className="container-custom py-4">
                            <nav className="flex flex-col gap-2">
                                {navLinks.map((link) => (
                                    <Link
                                        key={link.href}
                                        to={link.href}
                                        className={cn(
                                            "py-3 px-4 rounded-lg text-base font-medium transition-colors",
                                            location.pathname === link.href
                                                ? "bg-accent text-accent-foreground"
                                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                                        )}
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        {link.label}
                                    </Link>
                                ))}
                            </nav>
                        </div>
                    </div>
                )}
            </header>
        </>
    );
};

export default Header;
